import { useState } from 'react';
import { Lock, Unlock } from 'lucide-react';
import { useCloturerPeriode, usePeriodes, useRouvrirPeriode } from '@/hooks/useJournal';
import { useAuthStore } from '@/stores/authStore';
import { extractError } from '@/api/client';
import { demander } from '@/components/ui/Fenetres';

const MOIS = ['Janv.', 'Févr.', 'Mars', 'Avr.', 'Mai', 'Juin', 'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'];

/**
 * Mois comptables de l'exercice en cours (lot 2, étape 2.3, ADR-02).
 * Un mois clôturé n'accepte plus aucune écriture (refus posé dans la base).
 * Clôture : DAF (ou admin_holding) ; réouverture : admin_holding seul, motif obligatoire (D2.3-c).
 */
export const PeriodesBar = () => {
  const role = useAuthStore((s) => s.user?.role);
  const { data: exercices } = usePeriodes();
  const cloturer = useCloturerPeriode();
  const rouvrir = useRouvrirPeriode();
  const [erreur, setErreur] = useState<string | null>(null);
  const exercice = exercices?.[0];
  if (!exercice) return null;

  const peutCloturer = role === 'daf' || role === 'admin_holding';
  const peutRouvrir = role === 'admin_holding';

  const agir = async (id: string, statut: string, libelle: string) => {
    setErreur(null);
    try {
      if (statut === 'ouverte') {
        if (!peutCloturer || !(await demander.confirmer({ titre: `Clôturer ${libelle} ?`, message: 'Plus aucune écriture ne pourra y être validée.', confirmer: 'Clôturer', danger: true }))) return;
        await cloturer.mutateAsync(id);
      } else {
        if (!peutRouvrir) return;
        const motif = await demander.texte({ titre: `Rouvrir ${libelle}`, libelle: 'Motif (obligatoire, tracé dans l’historique)', min: 5, confirmer: 'Rouvrir', danger: true });
        if (motif === null) return;
        await rouvrir.mutateAsync({ id, motif });
      }
    } catch (e) {
      setErreur(extractError(e).message);
    }
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginRight: 4 }}>Exercice {exercice.annee}</span>
        {exercice.periodes.map((p) => {
          const close = p.statut === 'cloturee';
          const libelle = `${MOIS[p.numero - 1]} ${exercice.annee}`;
          const actif = close ? peutRouvrir : peutCloturer;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => void agir(p.id, p.statut, libelle)}
              disabled={!actif || cloturer.isPending || rouvrir.isPending}
              title={close
                ? `${libelle} clôturé${p.cloturePar ? ` par ${p.cloturePar.fullName}` : ''}${peutRouvrir ? ' — cliquer pour rouvrir' : ''}`
                : `${libelle} ouvert · ${p._count?.ecritures ?? 0} écriture(s)${peutCloturer ? ' — cliquer pour clôturer' : ''}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, fontSize: 11,
                border: '1px solid var(--bdr)', cursor: actif ? 'pointer' : 'default',
                background: close ? 'var(--bg)' : 'var(--surface)', color: close ? 'var(--tx-3)' : 'var(--tx-2)',
              }}
            >
              {close ? <Lock size={11} /> : <Unlock size={11} />} {MOIS[p.numero - 1]}
            </button>
          );
        })}
      </div>
      {erreur && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--danger)' }}>{erreur}</div>}
    </div>
  );
};
